import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { message, Badge, Form, Tag, Modal, Popover, Radio, Tooltip } from 'antd';
import { RequestSelect, Switch } from 'components/Form';
import coursesApiClient from 'utils/coursesApiClient';
import apiClient from 'utils/apiClient';
import Spinner from 'components/Spinner';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Search, Table, TableSelectedRowCount, Button, LocaleText } from 'ui';
import useHandleError from 'utils/useHandleError';
import generalHelpers from 'utils/generalHelpers';
import { FilterModal, SendEmailModal } from 'components/Modals';
import {ExclamationCircleOutlined} from "@ant-design/icons";
import DefaultLayout from '../../DefaultLayout';
import './styles.scss';
import moment from 'moment';
import { config } from '../../../config/config';
import ResultModal from './Modals/ResultModal';
import TermModal from './TermModal';
import ActivityStatusModal from './Modals/ActivityStatusModal';
import FlexRow from '../../FlexRow';
import useQueryApiClient from 'utils/useApiClient';
import FileDownload from 'js-file-download';
import { useLocaleContext } from 'context/LocaleContext';
import { useT } from 'hooks/Locale/useT';
import { AbsenceTag } from 'components/Tags';

const mapStateToProps = (state: any) => {
    return {
        session: state.session,
    };
};

interface StudentsInterface {
    program: any;
    session?: any;
    launchButton: HTMLElement;
}

const tagColors = {
    NOT_STARTED: 'gold',
    IN_PROGRESS: 'blue',
    RETURNED: 'blue',
    COMPLETED: 'green',
    PASSED: 'green',
    MISSED: 'red',
    CANCELLED: 'red',
    FAILED: 'red',
    PLANNED: 'geekblue',
    ATTENDED: 'green',
    NOT_APPLICABLE: 'gold',
};

const Students: React.FC<StudentsInterface> = ({ session, program, launchButton }) => {
    const { locale } = useLocaleContext();
    
    const [isLoading, setIsLoading] = useState(false);
    const [reload, setReload] = useState(false);
    const [search, setSearch] = useState('');
    const [visibleTooltip, setVisibleTooltip] = useState<number | null>(null);
    const [showUsersFilterModal, setShowUsersFilterModal] = useState(false);
    const [filter, setFilter] = useState('');
    const [buttonLoad, setButtonLoad] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<[]>([]);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [organizationsLoaded, setOrganizationsLoaded] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [showResultModal, setShowResultModal] = useState(false);
    const [showStudentFilterModal, setShowStudentFilterModal] = useState(false);
    const [activity, setActivity] = useState<{
        enrollmentId: number;
        activityId: number;
        fullName: string;
        title: string;
        type: string;
    }>({
        enrollmentId: 0,
        activityId: 0,
        fullName: '',
        type: '',
        title: '',
    });
    const [showActivityStatusModal, setShowActivityStatusModal] = useState(false);
    const [form] = Form.useForm();
    const [selectedCustomerId, setSelectedCustomerId] = useState<string>();
    const [dataSource, setDataSource] = useState<any>([]);
    const [scenarioUpdate, setScenarioUpdate] = useState(false);
    const [showMailModal, setShowMailModal] = useState(false);
    const [showTermModal, setShowTermModal] = useState(false);
    const [userTypeIds, setUserTypeIds] = useState<any>([]);
    const [userSync, setUserSync] = useState<boolean>(false);
    const {confirm} = Modal;

    useEffect(() => {
        if (selectedCustomerId === undefined) {
            form.setFieldsValue({
                organizationalUnits: undefined,
                departments: undefined,
            });
        }
    }, [selectedCustomerId]);

    const loadData = async (expanded: boolean, record: any) => {
        if (expanded) {
            setScenarioUpdate(true);
            try {
                const response = await coursesApiClient.request(
                    `/api/v1/courses/learning-program/${programId}/students/${record.enrollmentId}/activities`,
                    [],
                    'GET'
                );
                // record
                let key: any = Object.entries(dataSource).filter(
                    (el: any) => el[1].enrollmentId === record.enrollmentId
                );

                dataSource[key[0][0]].data = response.data;

                setDataSource(dataSource);

                setScenarioUpdate(false);
            } catch (e) {}
        }
    };

    const handleExpand = (expanded: boolean, record: any) => {
        loadData(expanded, record)
    }

    const defaultExpandable = {
        onExpand: (expanded: boolean, record: any) => expanded ? handleExpand(expanded, record) : null,
        expandedRowRender: (record: any, _index: number, _indent: number, expanded: boolean) => {
            if (expanded && !record?.data && !scenarioUpdate) {
                handleExpand(expanded, record);
            }

            if (record?.data) {
                return Object.values(record.data).map((activity: any) => {
                    let title = '';
                    if (activity.type === 'ACTIVITY') {
                        title =
                            activity.title[locale] ??
                            activity.title[Object.keys(activity.title)[0]];
                    } else {
                        const activityTitle = activity.activityTitle ?? activity.title;
                        if (typeof activityTitle === 'string') {
                            try {
                                const parsed = JSON.parse(activityTitle);
                                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                                    title = generalHelpers.getLocaleTextString(parsed, locale);
                                } else {
                                    title = activityTitle;
                                }
                            } catch {
                                title = activityTitle;
                            }
                        } else {
                            title = generalHelpers.getLocaleTextString(activityTitle, locale);
                        }
                    }

                    return (
                        <div
                            onClick={() =>
                                openResultsModal(
                                    record.enrollmentId,
                                    activity.id,
                                    record.name + ' ' + record.surname,
                                    title,
                                    activity.type
                                )
                            }
                            className={'flex justify-between student-result-wrapper pointer'}
                        >
                            {title}
                            <span>
                                {manualStatusWarning(activity)}
                                {tagBuilder(activity.status)}
                            </span>
                        </div>
                    );
                });
            } else {
                return <Spinner className={'ml-2'} size="small" spinning={true} />;
            }
        },
    };

    const [handleError] = useHandleError();
    const t = useT();
    const params = useParams();
    const programId = params.id;
    const intl = useIntl();

    useEffect(() => {
        managingOrganizations();
    }, []);

    const handleVisibleChange = (visible: boolean, recordId: number) => {
        if (visible) {
            setVisibleTooltip(recordId);
        } else {
            setVisibleTooltip(null);
        }
    };

    const openResultsModal = (
        enrollmentId: number,
        activityId: number,
        fullName: string,
        title: string,
        type: string
    ) => {
        setShowResultModal(true);
        setActivity({
            enrollmentId: enrollmentId,
            activityId: activityId,
            fullName: fullName,
            type: type,
            title: title,
        });
    };

    const addUsers = async (usersToEnroll: string) => {
        setButtonLoad(true);
        try {
            await coursesApiClient.request(
                `/api/v1/learning-program/${programId}/enroll?${usersToEnroll}`,
                [],
                'POST'
            );
            setReload(true);
        } catch (error) {
            handleError(error);
        } finally {
            setButtonLoad(false);
        }
    };

    const getDeadline = (record: any) => {
        if (program.status === 'DRAFT') {
            return '-';
        }
        let enrollDate;

        switch (program.type) {
            case 'TIMED_EVENT':
                let deadline;

                if (moment(record.createdAt) > moment(program.beginDate)) {
                    enrollDate = moment(record.createdAt);
                } else {
                    enrollDate = moment(program.beginDate);
                }

                if (
                    moment(enrollDate).add(program.maxCompletionTerm, 'days') >
                    moment(program.endDate)
                ) {
                    deadline = moment(enrollDate).add(program.termLimitValue, 'days');
                } else {
                    deadline = moment(enrollDate).add(program.maxCompletionTerm, 'days');
                }

                return deadline.format(config.defaultDateFormat);
            case 'CERTIFICATION':
                if (moment(record.createdAt) > moment(program.beginDate)) {
                    enrollDate = moment(record.createdAt);
                } else {
                    enrollDate = moment(program.beginDate);
                }

                return moment(enrollDate)
                    .add(program.maxCompletionTerm, 'days')
                    .format(config.defaultDateFormat);
        }
    };

    const handleRowActionVisibleChange = (visible: boolean, recordId: number) => {
        if (visible) {
            setVisibleTooltip(recordId);
        } else {
            setVisibleTooltip(null);
        }
    };

    const { appendData: handleActivitiesRegeneration } = useQueryApiClient({
        request: {
            disableOnMount: true,
            method: 'POST',
            url: `/api/v1/courses/learning-program/${programId}/enrollment/:enrollmentId/activities`,
        },
        onSuccess: (response) => {
            setReload(true);
        },
    });

    const { appendData: forcheCheck } = useQueryApiClient({
        request: {
            disableOnMount: true,
            method: 'POST',
            url: `/api/v1/courses/learning-program/options/enrollment/:id`,
        },
        onSuccess: () => {
            setReload(true);
        },
    });

    const { appendData: checkCertificates } = useQueryApiClient({
        request: {
            disableOnMount: true,
            method: 'POST',
            url: '/api/v1/courses/learning-program/options/enrollment/:id/certificate',
        },
        onSuccess: () => {
            setReload(true);
        },
    });

    const columns = [
        {
            title: intl.formatMessage({ id: 'general.name' }),
            sorter: true,
            key: 'name',
            dataIndex: 'name',
        },
        {
            title: intl.formatMessage({ id: 'general.surname' }),
            sorter: true,
            key: 'surname',
            dataIndex: 'surname',
        },
        {
            title: intl.formatMessage({ id: 'general.position' }),
            sorter: true,
            dataIndex: 'position',
            key: 'position',
        },
        {
            title: intl.formatMessage({ id: 'general.organizational_unit' }),
            sorter: true,
            dataIndex: 'organizationalUnit',
            key: 'organizationalUnit',
        },
        {
            title: intl.formatMessage({ id: 'general.organization' }),
            sorter: true,
            dataIndex: 'organization',
            key: 'organization',
        },
        // {
        //     title: intl.formatMessage({id: 'general.progress'}),
        //     render: (_text: string, record: any) => {
        //         return (
        //             <div>
        //                 <Progress showInfo={false} trailColor={'#D9D9D9'} strokeColor={'#52c41a'}
        //                           percent={((record.passed + record.passedLecture) / (record.totalLecture + record.total)) * 100}/>
        //             </div>
        //         )
        //     },
        // },
        {
            title: intl.formatMessage({ id: 'general.status' }),
            sorter: true,
            key: 'active_status',
            render: (_text: string, record: any) =>
                tagBuilder(record.activeStatus, !!record.isCancelled),
        },
        {
            title: intl.formatMessage({ id: 'general.deadline' }),
            sorter: true,
            dataIndex: 'deadline',
            key: 'active_deadline',
            render: (deadline: string, record: any) =>
                !!deadline
                    ? moment(deadline).format(config.defaultDateFormat)
                    : getDeadline(record),
        },
        {
            title: intl.formatMessage({ id: 'general.is_completed' }),
            sorter: true,
            dataIndex: 'completedAt',
            key: 'completed_at',
            render: (isCompleted: string) =>
                !!isCompleted ? moment(isCompleted).format(config.defaultDateFormat) : null,
        },
        {
            title: intl.formatMessage({ id: 'general.valid_until' }),
            sorter: true,
            dataIndex: 'expirationDate',
            key: 'valid_to',
            render: (expirationDate: string) =>
                !!expirationDate ? moment(expirationDate).format(config.defaultDateFormat) : '-',
        },
        {
            title: intl.formatMessage({ id: 'general.last_activity' }),
            sorter: true,
            dataIndex: 'lastActivity',
            key: 'last_activity',
            render: (lastActivity: string) =>
                !!lastActivity ? moment(lastActivity).format(config.defaultDateFormat) : null,
        },
        {
            title: intl.formatMessage({ id: 'general.enrollment_type' }),
            dataIndex: 'enrollmentType',
            key: 'enrollmentType',
            render: (enrollmentType: any, record: any) => {
                switch (enrollmentType) {
                    case 'SELF_ENROLLMENT':
                    case 'INVITE_ENROLLMENT':
                    case 'MANUAL_ENROLLMENT':
                    case 'IMPORT_ENROLLEMNT':
                        return intl.formatMessage({
                            id: 'program.enrollment.' + enrollmentType.toLowerCase(),
                        });
                    case 'AUDIENCE_ENROLLMENT':
                        if (!record.audienceEnrollment) {
                            return;
                        }
                        return generalHelpers.replaceAll(record.audienceEnrollment, ',', ';') + ';';
                    case 'ENROLLMENT_CANCELLED':
                        return '-';
                }
            },
        },
        {
            title: intl.formatMessage({ id: 'users.form.long_absence' }),
            dataIndex: 'absence',
            key: 'absence',
            render: (record: null | string) => <AbsenceTag status={!!record}/>
        },
        {
            title: intl.formatMessage({ id: 'program.recertification' }),
            sorter: true,
            dataIndex: 'recertification',
            key: 'recertification',
            render: (recertification: boolean, record: any) => {
                return (
                    <Switch
                        checked={recertification}
                        onChange={() => handleSwitch([record.enrollmentId])}
                        disabled={!!record.isCancelled}
                    />
                );
            },
        },
        {
            title: intl.formatMessage({id: 'general.actions'}),
            key: 'actions',
            render: (_text: string, record: any) => {
                const content = (
                    <>
                        <div className="popover-item" onClick={() => {
                            handleActivitiesRegeneration({}, {enrollmentId: record.enrollmentId})
                            setVisibleTooltip(null);
                        }}>
                            <FormattedMessage id="general.regenerate_activities"/>
                        </div>
                        <div className="popover-item" onClick={() => {
                            forcheCheck({}, {id: record.enrollmentId})
                            setVisibleTooltip(null);
                        }}>
                            <FormattedMessage id="general.force_check"/>
                        </div>
                        <div className="popover-item" onClick={() => {
                            checkCertificates({}, {id: record.enrollmentId})
                            setVisibleTooltip(null);
                        }}>
                            <FormattedMessage id="general.check_certificate"/>
                        </div>
                    </>
                );

                return (
                    <Popover
                        open={record.enrollmentId === visibleTooltip}
                        content={content}
                        trigger="click"
                        placement="bottomRight"
                        arrowPointAtCenter
                        onOpenChange={(visible) => handleRowActionVisibleChange(visible, record.enrollmentId)}
                    >
                        <div style={{width: '100%', cursor: 'pointer', textAlign: 'center'}}>
                            <i className="fal fa-ellipsis-v" style={{fontSize: '16px'}}/>
                        </div>
                    </Popover>
                )
            },
        }
    ];

    const handleSwitch = async (ids: number[]) => {
        try {
            setScenarioUpdate(true);

            await coursesApiClient.request(
                `/api/v1/courses/learning-program/${programId}/update-recertification`,
                { enrollmentIds: ids },
                'POST'
            );

            setScenarioUpdate(false);
            setReload(true);
        } catch (error) {
            handleError(error);
        }
    };

    const managingOrganizations = async () => {
        setIsLoading(true);
        if (
            session.active_user_type === 'CUSTOMER_ADMIN' ||
            session.active_user_type === 'SUPERVISION'
        ) {
            setOrganizationsLoaded(session.organization.organization_id);
            setIsLoading(false);
            return;
        }

        try {
            const organizations = await apiClient.request(
                '/api/v1/organizations/options/managing-organizations',
                [],
                'GET'
            );
            setOrganizationsLoaded(Object.keys(organizations.customers).toString());
        } catch (error) {
            handleError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const addUSerToProgram = async () => {
        try {
            await coursesApiClient.request(
                `/api/v1/courses/learning-program/${programId}/enrollment`,
                {
                    userTypeId: selectedUser,
                },
                'POST'
            );
            setReload(true);
        } catch (error) {
            handleError(error);
        }
    };

    /**
     * CSV-1927: an admin's manual status can be superseded by the student's next action
     * (re-entering the test). Without this marker the tag looks like the change never saved.
     */
    const manualStatusWarning = (activity: any) => {
        if (!activity?.lastManualStatus || activity.lastManualStatus === activity.status) {
            return null;
        }

        const tooltip = t('program.manual_status_overwritten', {
            status: intl.formatMessage({
                id: `general.${String(activity.lastManualStatus).toLowerCase()}`,
            }),
            date: activity.lastManualStatusAt
                ? moment(activity.lastManualStatusAt).format(config.defaultFullDateFormat)
                : '-',
        });

        return (
            <Tooltip title={tooltip}>
                <ExclamationCircleOutlined className={'mr-1'} />
            </Tooltip>
        );
    };

    const tagBuilder = (
        status:
            | 'NOT_STARTED'
            | 'IN_PROGRESS'
            | 'COMPLETED'
            | 'PASSED'
            | 'MISSED'
            | 'CANCELLED'
            | 'FAILED'
            | 'RETURNED'
            | 'NOT_APPLICABLE'
            | null,
        isCancelled = false
    ) => {
        let tagStatus = status ? status : 'NOT_STARTED';

        if (status !== 'COMPLETED' && isCancelled) {
            tagStatus = 'CANCELLED';
        }

        return (
            <Tag color={tagColors[tagStatus]}>
                {intl.formatMessage({
                    id:
                        status === 'RETURNED'
                            ? 'courses.topic_status.submission_returned'
                            : `general.${tagStatus.toLowerCase()}`,
                })}
            </Tag>
        );
    };

    const parseMailingData: any = () => {
        let data: any = [];
        userTypeIds.map((el: any) => {
            data.push({
                userTypeId: el.userTypeId,
                student_deadline: !!el.deadline
                    ? moment(el.deadline).format(config.defaultDateFormat)
                    : getDeadline(el),
                program_title: program.title,
            });
        });

        return data;
    };

    const generateTitle = () => {
        return `${
            programId && program
                ? program.title[locale]
                    ? program.title[locale]
                    : program.title[Object.keys(program.title)[0]]
                : 'export'
        }_${moment().format('YYYY-MM-DD')}.xlsx`;
    };

    const { appendData: cancelEnrollment } = useQueryApiClient({
        request: {
            url: `/api/v1/courses/learning-program/${programId}/un-enroll`,
            baseUrl: config.api.coursesUrl,
            method: 'POST'
        },
        onSuccess: () => {
            message.success(intl.formatMessage({id: 'program.un_enroll_success'}));
            setReload(true);
        }
    });

    const { appendData: reEnroll } = useQueryApiClient({
        request: {
            url: `/api/v1/courses/learning-program/${programId}/re-enroll`,
            baseUrl: config.api.coursesUrl,
            method: 'POST'
        },
        onSuccess: () => {
            message.success(intl.formatMessage({id: 'program.re_enroll_success'}));
            setReload(true);
        }
    });

    const { fetch: handleExport } = useQueryApiClient({
        request: {
            disableOnMount: true,
            method: 'GET',
            multipart: true,
            url: `/api/v1/courses/learning-program/${programId}/students/export`,
        },
        onSuccess: (response) => {
            FileDownload(response, generateTitle());
        },
    });

    useQueryApiClient({
        request: {
            disableOnMount: config.api.environment !== 'development',
            url: `/api/v1/courses/learning-program/${programId}/students/status`,
        },
        onSuccess: (response) => {
            setUserSync(response.data);
        },
    });

    const warnDeletion = async (type: 'RE_ENROLL' | 'CANCEL_ENROLLMENT') => {
        let reEnrollMode: 'NEW' | 'REENROLL' = 'REENROLL';

        confirm({
            title: intl.formatMessage({ id: 'general.attention' }),
            content: type === 'RE_ENROLL'
                ? (
                    <>
                        <div style={{marginBottom: 12}}>
                            {intl.formatMessage({id: 'program.re_enroll'})}
                        </div>
                        <Radio.Group
                            defaultValue="REENROLL"
                            onChange={(e) => { reEnrollMode = e.target.value; }}
                        >
                            <Radio value="REENROLL">
                                {intl.formatMessage({id: 'program.re_enroll_mode_continue'})}
                            </Radio>
                            <Radio value="NEW">
                                {intl.formatMessage({id: 'program.re_enroll_mode_new'})}
                            </Radio>
                        </Radio.Group>
                    </>
                )
                : intl.formatMessage({id: 'program.cancel_enrollment'}),
            icon: <ExclamationCircleOutlined />,
            okText: intl.formatMessage({ id: 'general.continue' }),
            cancelText: intl.formatMessage({ id: 'general.cancel' }),

            onOk() {
                switch (type) {
                    case 'CANCEL_ENROLLMENT':
                        cancelEnrollment({enrollmentIds: selectedRowKeys})
                        break;
                    case 'RE_ENROLL':
                        reEnroll({enrollmentIds: selectedRowKeys, mode: reEnrollMode})
                        break;
                }
            }
        });
    }

    return (
        <>
            <Spinner spinning={isLoading}>
                <FlexRow
                    left={
                        <>
                            <Button
                                disabled={
                                    !(
                                        program?.visualPermissions?.edit ||
                                        program?.visualPermissions?.admin
                                    )
                                }
                                loading={buttonLoad}
                                type="primary"
                                onClick={() => setShowUsersFilterModal(true)}
                                message={'campaign.enroll'}
                            />
                            {organizationsLoaded && (
                                <>
                                    <RequestSelect
                                        disabled={
                                            !(
                                                program?.visualPermissions?.edit ||
                                                program?.visualPermissions?.admin
                                            )
                                        }
                                        isForm={false}
                                        customLayout={true}
                                        selectClassName="request-select"
                                        url={`api/v1/edu/users/search-student`}
                                        param={[`customerId=${organizationsLoaded}&key=id&accountStatus=ACTIVE`]}
                                        onChange={(typeUuid: string) => setSelectedUser(typeUuid)}
                                        column
                                        showSearch
                                    />
                                    <Button
                                        disabled={
                                            !(
                                                program?.visualPermissions?.edit ||
                                                program?.visualPermissions?.admin
                                            )
                                        }
                                        type="primary"
                                        onClick={() => addUSerToProgram()}
                                        message={'general.add'}
                                    />
                                </>
                            )}
                            {config.api.environment === 'development' ? (
                                <Badge status={userSync ? 'processing' : 'success'} />
                            ) : null}
                        </>
                    }
                    right={
                        <>
                            <Button type="default" onClick={() => handleExport()} message={'general.export'}/>
                            <Button type="default" onClick={() => setShowStudentFilterModal(true)} message={'general.filter'} />
                            <Search setReload={setReload} setSearchString={setSearch} />
                        </>
                    }
                />
                <div className={'table-filter-modal'}>
                    <FilterModal
                        visible={showStudentFilterModal}
                        onCancel={setShowStudentFilterModal}
                        title={'general.filter_by'}
                        okProp={'campaign.form.filter_title'}
                        modules={{
                            programStatus: { mode: 'multiple', visible: true },
                            customerSelect: {
                                mode: 'multiple',
                                visible: generalHelpers.isAdmin(session.active_user_type),
                            },
                            organizationalUnitId: {
                                mode: 'multiple',
                                visible: true,
                                customerOnly: true,
                            },
                            departmentId: {
                                mode: 'multiple',
                                visible: true,
                                customerOnly: true,
                            },
                            recertification: { default: [1, 0], visible: true },
                            expirationDate: { visible: true },
                            absence: { visible: true },

                            // activity: {mode: 'multiple', visible: true},
                            // lecture: {mode: 'multiple', visible: true},
                            // activityStatus: {mode: 'multiple', visible: true},
                        }}
                        setFilter={setFilter}
                        filter={filter}
                        load={setReload}
                        locale={locale}
                    />
                </div>

                <div className={'program-student-table'}>
                    {program && (
                        <Table
                            tableKey={'programStudents'}
                            rowClassName={(record: any) =>
                                record.accountStatus === 'BLOCKED' ? 'table-row-red' : ''
                            }
                            expandable={defaultExpandable}
                            columns={
                                (program.type === 'CERTIFICATION'
                                    ? columns
                                    : columns.filter(
                                          (el: any) => el.dataIndex !== 'recertification'
                                      ))
                                    .filter((el: any) => program?.visualPermissions?.power ? true : el.key !== 'actions' )
                            }
                            url={`/api/v1/courses/learning-program/${programId}/students`}
                            setReload={setReload}
                            reload={reload}
                            rowSelection={
                                program?.visualPermissions?.edit ||
                                program?.visualPermissions?.admin
                            }
                            selectedRowKeys={selectedRowKeys}
                            setSelectedRowKeys={setSelectedRowKeys}
                            setTotalRecords={setTotalRecords}
                            manualDataUpdate={scenarioUpdate}
                            updateData={dataSource}
                            saveData={setDataSource}
                            additionalSelect={setUserTypeIds}
                            additionalSelectKey={'userTypeId'}
                            wholeRow={true}
                            rowKey={'enrollmentId'}
                            scroll={{ x: 800 }}
                            filter={filter}
                            search={search}
                        />
                    )}
                </div>
            </Spinner>
            <FilterModal
                okProp={'campaign.enroll'}
                visible={showUsersFilterModal}
                onCancel={setShowUsersFilterModal}
                modules={{
                    customerSelect: {
                        mode: 'default',
                        visible: generalHelpers.isAdmin(session.active_user_type),
                    },
                    organizationalUnitId: { visible: true },
                    departmentId: {mode: 'multiple', visible: true},
                    studentPosition: { visible: true },
                    newUsers: { default: false, visible: true },
                    customerFilter: { mode: 'multiple', visible: true },
                }}
                submit={addUsers}
            />
            <DefaultLayout.PageFooter
                left={
                    <>
                        <TableSelectedRowCount rows={selectedRowKeys} />
                        {program && program.status === 'ONGOING' && (
                            <>
                                <Button
                                    disabled={
                                        !(
                                            program?.visualPermissions?.edit ||
                                            program?.visualPermissions?.admin
                                        )
                                    }
                                    onClick={() => setShowActivityStatusModal(true)}
                                    message={'program.change_activity_status'}
                                />
                            </>
                        )}
                        {program && program.status !== 'FINISHED' && (
                            <>
                                <Button
                                    disabled={
                                        !(
                                            program?.visualPermissions?.edit ||
                                            program?.visualPermissions?.admin
                                        )
                                    }
                                    onClick={() => warnDeletion('CANCEL_ENROLLMENT')}
                                    message={'program.cancel_enrollment'}
                                />
                                <Button
                                    disabled={
                                        !(
                                            program?.visualPermissions?.edit ||
                                            program?.visualPermissions?.admin
                                        )
                                    }
                                    onClick={() => warnDeletion('RE_ENROLL')}
                                    message={'program.re_enroll'}
                                />
                                <Button onClick={() => setShowMailModal(true)}
                                    message={'users.send_email_users'}
                                    icon={'fal fa-envelope'}
                                />
                                {generalHelpers.hasSpecialPermissions(session, 'edit_terms')  ? 
                                    <Button onClick={() => setShowTermModal(true)}
                                        message={'program.edit_terms'}
                                    />
                                : null
                                }
                            </>
                        )}
                    </>
                }
                right={<>{launchButton}</>}
            />
            {!!(program?.id && selectedRowKeys.length) && (
                <TermModal
                    reloadStudents={setReload}
                    students={selectedRowKeys}
                    open={showTermModal}
                    program={program}
                    data={dataSource}
                    onCancel={setShowTermModal}
                />
            )}
            {(program?.id) && (
                <ResultModal
                    programId={program.id}
                    open={showResultModal}
                    onCancel={setShowResultModal}
                    activity={activity}
                />
            )}
            <ActivityStatusModal
                reloadStudents={setReload}
                students={selectedRowKeys}
                open={showActivityStatusModal}
                onCancel={setShowActivityStatusModal}
            />
            {(program?.ownerUuid) && (
                <SendEmailModal
                    visible={showMailModal}
                    onCancel={setShowMailModal}
                    organizationId={program.ownerUuid}
                    organizationType={program.ownerType}
                    defaultCategory={'learning_program'}
                    users={[]}
                    programId={program.id}
                    programData={parseMailingData()}
                />
            )}
        </>
    );
};
export default connect(mapStateToProps)(Students);
